import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CurrentSubscription = ({ subscriptionData, onPlanUpgrade }) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');

  const formatDate = (dateString) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })?.format(new Date(dateString));
  };

  const getPlanDetails = () => {
    const planMap = {
      free: {
        name: 'Artiste',
        price: 0,
        color: 'text-muted-foreground',
        icon: 'User'
      },
      professional: {
        name: 'Professionnel',
        price: 29,
        color: 'text-accent',
        icon: 'Crown'
      },
      enterprise: {
        name: 'Entreprise',
        price: 99,
        color: 'text-primary',
        icon: 'Building'
      }
    };
    
    return planMap?.[subscriptionData?.planType] || planMap?.free;
  };

  const planDetails = getPlanDetails();

  const usagePercentage = (used, limit) => {
    if (!limit || limit === 'unlimited') return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'text-error';
    if (percentage >= 70) return 'text-warning';
    return 'text-success';
  };

  const handleCancelSubscription = () => {
    console.log('Cancelling subscription:', cancellationReason);
    setShowCancelModal(false);
    // Here you would process the cancellation
  };

  return (
    <div className="space-y-8">
      {/* Current Plan Overview */}
      <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center justify-center w-12 h-12 rounded-lg ${
              subscriptionData?.planType === 'free' ? 'bg-muted' : 'bg-accent/10'
            }`}>
              <Icon name={planDetails?.icon} size={24} className={planDetails?.color} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-card-foreground">
                Plan {planDetails?.name}
              </h2>
              <p className="text-muted-foreground">
                {subscriptionData?.planType === 'free' ?'Plan gratuit - Fonctionnalités de base' 
                  : `${planDetails?.price}€/mois - Fonctionnalités premium`
                }
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              subscriptionData?.planType === 'free' ?'bg-muted text-muted-foreground' :'bg-success/10 text-success'
            }`}>
              <Icon name="Circle" size={8} className="mr-2 fill-current" />
              {subscriptionData?.planType === 'free' ? 'Gratuit' : 'Actif'}
            </div>
          </div>
        </div>

        {/* Renewal Information */}
        {subscriptionData?.planType !== 'free' && (
          <div className="bg-muted/50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="Calendar" size={18} className="text-accent" />
                <div>
                  <p className="text-sm font-medium text-card-foreground">
                    Prochain renouvellement
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(subscriptionData?.renewalDate)}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-card-foreground">
                  {planDetails?.price}€
                </p>
                <p className="text-xs text-muted-foreground">
                  Renouvellement automatique
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          {subscriptionData?.planType === 'free' ? (
            <Button
              variant="default"
              iconName="Crown"
              iconPosition="left"
              iconSize={16}
              onClick={() => onPlanUpgrade('professional')}
            >
              Passer à Premium
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                iconName="CreditCard"
                iconPosition="left"
                iconSize={16}
                onClick={() => console.log('Modify payment')}
              >
                Modifier le paiement
              </Button>
              <Button
                variant="outline"
                iconName="Settings"
                iconPosition="left"
                iconSize={16}
                onClick={() => console.log('Manage subscription')}
              >
                Gérer l'abonnement
              </Button>
              <Button
                variant="outline"
                iconName="Pause"
                iconPosition="left"
                iconSize={16}
                onClick={() => console.log('Pause subscription')}
              >
                Suspendre
              </Button>
            </>
          )}
        </div>
      </div>
      {/* Usage Statistics */}
      <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-card-foreground mb-6">
          Utilisation ce mois-ci
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Vues de profil</span>
              <span className="text-sm font-medium text-card-foreground">
                {subscriptionData?.usage?.profileViews?.toLocaleString()}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-accent h-2 rounded-full transition-all duration-300"
                style={{ width: '75%' }}
              ></div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Messages envoyés</span>
              <span className="text-sm font-medium text-card-foreground">
                {subscriptionData?.usage?.messagesSent}
                {subscriptionData?.planType === 'free' && (
                  <span className="text-muted-foreground"> / 10</span>
                )}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  subscriptionData?.planType === 'free' ? 'bg-warning' : 'bg-success'
                }`}
                style={{ 
                  width: subscriptionData?.planType === 'free' 
                    ? `${Math.min((subscriptionData?.usage?.messagesSent / 10) * 100, 100)}%`
                    : '60%'
                }}
              ></div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Recherches</span>
              <span className="text-sm font-medium text-card-foreground">
                {subscriptionData?.usage?.searchQueries}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: '45%' }}
              ></div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Analytics</span>
              <span className={`text-sm font-medium ${
                subscriptionData?.usage?.analyticsAccess ? 'text-success' : 'text-muted-foreground'
              }`}>
                {subscriptionData?.usage?.analyticsAccess ? 'Activé' : 'Indisponible'}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  subscriptionData?.usage?.analyticsAccess ? 'bg-success' : 'bg-muted-foreground'
                }`}
                style={{ width: subscriptionData?.usage?.analyticsAccess ? '100%' : '0%' }}
              ></div>
            </div>
          </div>
        </div>

        {subscriptionData?.planType === 'free' && subscriptionData?.usage?.messagesSent >= 8 && (
          <div className="mt-6 bg-warning/10 border border-warning/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={18} className="text-warning mt-0.5" />
              <div>
                <p className="text-sm font-medium text-warning">
                  Limite proche
                </p>
                <p className="text-xs text-warning/80 mt-1">
                  Vous avez utilisé {subscriptionData?.usage?.messagesSent} messages sur 10. 
                  Passez à Premium pour un accès illimité.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Active Features */}
      <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-card-foreground mb-6">
          Fonctionnalités actives
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries({
            'Messages illimités': subscriptionData?.features?.unlimitedMessaging,
            'Support prioritaire': subscriptionData?.features?.prioritySupport,
            'Analytics avancées': subscriptionData?.features?.advancedAnalytics,
            'Boost de profil': subscriptionData?.features?.profileBoost,
            'Événements exclusifs': subscriptionData?.features?.exclusiveEvents
          })?.map(([feature, active]) => (
            <div
              key={feature}
              className={`flex items-center justify-between p-3 rounded-lg ${
                active ? 'bg-success/10' : 'bg-muted/50'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Icon 
                  name={active ? "CheckCircle" : "Circle"} 
                  size={16} 
                  className={active ? "text-success" : "text-muted-foreground"} 
                />
                <span className={`text-sm ${
                  active ? 'text-card-foreground' : 'text-muted-foreground'
                }`}>
                  {feature}
                </span>
              </div>
              {!active && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Crown"
                  iconPosition="left"
                  iconSize={12}
                  onClick={() => onPlanUpgrade('professional')}
                  className="text-xs"
                >
                  Débloquer
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Cancellation Section */}
      {subscriptionData?.planType !== 'free' && (
        <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-card-foreground mb-4">
            Gérer votre abonnement
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3 p-4 bg-muted/50 rounded-lg">
              <Icon name="Info" size={18} className="text-primary mt-0.5" />
              <div className="space-y-2">
                <p className="text-sm text-card-foreground">
                  Vous pouvez annuler votre abonnement à tout moment.
                </p>
                <p className="text-xs text-muted-foreground">
                  Votre accès premium restera actif jusqu'à la date de renouvellement. 
                  Aucun remboursement partiel ne sera effectué.
                </p>
              </div>
            </div>
            
            <Button
              variant="outline"
              iconName="XCircle"
              iconPosition="left"
              iconSize={16}
              onClick={() => setShowCancelModal(true)}
              className="text-error hover:text-error"
            >
              Annuler l'abonnement
            </Button>
          </div>
        </div>
      )}
      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-error/10 rounded-full mx-auto mb-4">
                <Icon name="XCircle" size={24} className="text-error" />
              </div>
              <h3 className="text-lg font-semibold text-card-foreground mb-2">
                Annuler l'abonnement
              </h3>
              <p className="text-muted-foreground text-sm">
                Nous sommes désolés de vous voir partir. Aidez-nous à nous améliorer.
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="text-sm font-medium text-card-foreground mb-2 block">
                  Raison de l'annulation (optionnel)
                </label>
                <select
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="">Sélectionner une raison</option>
                  <option value="too-expensive">Trop cher</option>
                  <option value="not-using">Je n'utilise pas assez</option>
                  <option value="missing-features">Fonctionnalités manquantes</option>
                  <option value="technical-issues">Problèmes techniques</option>
                  <option value="other">Autre</option>
                </select>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowCancelModal(false)}
              >
                Garder l'abonnement
              </Button>
              <Button
                variant="destructive"
                fullWidth
                onClick={handleCancelSubscription}
              >
                Confirmer l'annulation
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentSubscription;